import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldRow, Input, Label } from '@sentinel/ui';

export type ResendStatus = 'idle' | 'sent' | 'rate_limited' | 'error';

export interface EmailPromptProps {
  /** Initial value only — the user can edit it before resending. */
  email?: string;
  onResend: (email: string) => void;
  pending: boolean;
  status: ResendStatus;
}

export function EmailPrompt({ email, onResend, pending, status }: EmailPromptProps) {
  const { t } = useTranslation('welcome');
  const [address, setAddress] = useState(email ?? '');

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = address.trim();
    if (!trimmed) return;
    onResend(trimmed);
  }

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <h1>{t('title')}</h1>
      {email ? (
        <p>{t('subtitle_with_email', { email })}</p>
      ) : (
        <p>{t('subtitle_generic')}</p>
      )}

      {status === 'sent' ? (
        <Alert type="success" role="status">
          {t('resent_success')}
        </Alert>
      ) : null}
      {status === 'rate_limited' ? (
        <Alert type="warning" role="alert">
          {t('resent_rate_limited')}
        </Alert>
      ) : null}
      {status === 'error' ? (
        <Alert type="error" role="alert">
          {t('resent_generic_error')}
        </Alert>
      ) : null}

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '0.5rem' }}>
        <FieldRow>
          <Label htmlFor="welcome-email">{t('email_label')}</Label>
          <Input
            id="welcome-email"
            data-testid="welcome-email-input"
            type="email"
            autoComplete="email"
            required
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </FieldRow>
        <Button
          type="submit"
          data-testid="welcome-resend-button"
          disabled={pending || address.trim() === ''}
        >
          {t('resend_button')}
        </Button>
      </form>
    </div>
  );
}
